import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, AlertCircle } from "lucide-react";
import { ContactPhoneType } from "@satyrsmc/shared/lib/enums";
import { MONTHS, toDateOnly } from "@satyrsmc/shared/lib/date-utils";
import { Button } from "@satyrsmc/shared/components";
import { trpc } from "@/trpc";
import type { RosterPrintMember } from "@satyrsmc/shared/dto/member/roster";

const PHONE_LABELS: Record<ContactPhoneType, string> = {
  [ContactPhoneType.Cell]: "Cell",
  [ContactPhoneType.Home]: "Home",
  [ContactPhoneType.Work]: "Work",
  [ContactPhoneType.Other]: "Phone",
};

function ordinal(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0]}`;
}

/** "March 14th, 1985" — timezone-safe, from an ISO / YYYY-MM-DD string. */
function formatLongDateOrdinal(iso: string): string {
  const d = toDateOnly(iso);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  const month = MONTHS[Number(m) - 1];
  if (!month) return "";
  return `${month} ${ordinal(Number(day))}, ${y}`;
}

function todayLongDateOrdinal(): string {
  const now = new Date();
  return `${MONTHS[now.getMonth()]} ${ordinal(now.getDate())}, ${now.getFullYear()}`;
}

export function RosterPrintPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = trpc.members.roster.printList.useQuery(undefined, {
    staleTime: 30_000,
  });

  function handleBack() {
    // Return to the exact roster the user came from (preserving filters/scroll)
    // when there's in-app history; otherwise fall back to the roster page.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate("/roster");
  }

  const content = (
    <div className="roster-print-portal fixed inset-0 z-[60] overflow-y-auto bg-white text-black">
      {/* Toolbar — never printed. Dark-blue to match the site nav (the shared
          Button's ghost/light text is designed for this background). */}
      <div className="print:hidden sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-700/60 bg-gradient-to-tr from-satyrs-dark to-satyrs-dark-alt/80 px-4 py-3 text-white">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1">
          <ArrowLeft className="size-4" />
          Back to Roster
        </Button>
        <Button size="sm" onClick={() => window.print()} className="gap-1" disabled={!data}>
          <Printer className="size-4" />
          Print
        </Button>
      </div>

      {/* Document */}
      <div className="mx-auto max-w-[8.5in] px-8 py-6 print:px-0 print:py-0">
        {/* Header */}
        <header className="mb-6 text-center">
          <h1 className="font-script text-3xl leading-tight text-satyrs-blue">Satyrs Roster</h1>
          <p className="font-script text-xl text-satyrs-blue">{todayLongDateOrdinal()}</p>
        </header>

        {isLoading ? (
          <p className="py-12 text-center text-gray-500">Loading roster…</p>
        ) : error ? (
          <div className="mx-auto flex max-w-md items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 size-5 text-red-600" />
            <div>
              <p className="font-medium text-red-700">Failed to load roster</p>
              <p className="mt-1 text-sm text-gray-600">{error.message}</p>
            </div>
          </div>
        ) : data && data.members.length > 0 ? (
          <div className="[column-gap:2.5rem] sm:columns-2">
            {data.members.map((member) => (
              <RosterPrintEntry key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <p className="py-12 text-center text-gray-500">No members to display.</p>
        )}

        {/* Footer logo */}
        <footer className="mt-8 flex justify-center">
          <img src="/images/satyrs-roster-logo.png" alt="Satyrs M/C" className="h-40 w-auto" />
        </footer>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function RosterPrintEntry({ member }: { member: RosterPrintMember }) {
  const { address } = member;
  const cityStateZip = address
    ? [address.city, [address.state, address.postal_code].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className="mb-5 break-inside-avoid">
      <p className="font-semibold">{member.display_name}</p>

      {address?.line1 && <p>{address.line1}</p>}
      {address?.line2 && <p>{address.line2}</p>}
      {cityStateZip && <p>{cityStateZip}</p>}

      {member.phones.map((phone) => (
        <p key={`${phone.type}-${phone.phone}`}>
          {PHONE_LABELS[phone.type]}: {phone.phone}
        </p>
      ))}

      {member.email && <p>E-mail: {member.email}</p>}

      {member.birthday && <p>Birthday: {formatLongDateOrdinal(member.birthday)}</p>}

      {member.emergency_contacts.map((ice) => (
        <div key={`${ice.name}-${ice.phone ?? "nophone"}`}>
          <p>
            ICE: {ice.name}
            {ice.relationship ? `, ${ice.relationship}` : ""}
          </p>
          {ice.phone && <p>{ice.phone}</p>}
        </div>
      ))}

      {/* Gold separator, matching the printed roster */}
      <div className="mt-3 h-[3px] w-full bg-satyrs-gold" aria-hidden="true" />
    </div>
  );
}
